/*
 * Copyright (c) 2026 SimplyBook.me Ltd. All rights reserved.
 * Licensed under the Apache License, Version 2.0.
 */

import React from "react";
import { useNavigate } from "react-router-dom";
import Container from "../../components/Container";
import NavigationHeader from "../../components/NavigationHeader";
import LoadingIndicator from "../../components/LoadingIndicator";
import Stack from "../../components/ui/Stack";
import Icon from "../../components/ui/Icon";
import NavigationCard from "../../components/ui/NavigationCard";
import { RECURRING_PROFILE_STATUS, ROUTES } from "../../constants/constants";
import { useConfig } from "../../lib/react-query/useConfig";
import { useUnpaidInvoicesCount } from "../../lib/react-query/useUnpaidInvoicesCount";
import { useRecurringProfiles } from "../../lib/react-query/useRecurringProfiles";
import { usePaymentMethods } from "../../lib/react-query/usePaymentMethods";
import T from "../../components/ui/Translation";

const ClientPayments = () => {
  const navigate = useNavigate();
  const { data: config } = useConfig();
  const rebillingEnabled = !!config?.allow_rebilling_payments;

  const { data: unpaidCount, isLoading: unpaidLoading } = useUnpaidInvoicesCount();
  const { data: profilesData, isLoading: profilesLoading } = useRecurringProfiles(1, undefined, 100);
  const { data: paymentMethods, isLoading: methodsLoading } = usePaymentMethods({ enabled: rebillingEnabled });

  const profiles = profilesData?.data || [];
  const activeProfilesCount = profiles.filter(
    (profile) => profile.status === RECURRING_PROFILE_STATUS.ACTIVE
  ).length;
  const methodsCount = paymentMethods?.length || 0;

  if (unpaidLoading || profilesLoading || (rebillingEnabled && methodsLoading)) {
    return (
      <Container>
        <NavigationHeader
          title="Payments"
          subtitle="Your billing in one place — review invoices, manage recurring payments, and keep your payment methods up to date."
          back={ROUTES.HOME}
        />
        <LoadingIndicator page />
      </Container>
    );
  }

  return (
    <Container>
      <NavigationHeader
        title="Payments"
        subtitle="Your billing in one place — review invoices, manage recurring payments, and keep your payment methods up to date."
        back={ROUTES.HOME}
      />
      <Stack $gap="1rem">
        <NavigationCard
          icon={<Icon.FileList size={Icon.SIZE.SM} />}
          title="Invoices"
          description="View, pay and download receipts for your bookings"
          badge={unpaidCount ? <T count={unpaidCount}>%count% unpaid</T> : undefined}
          badgeColor="error"
          onClick={() => navigate(ROUTES.CLIENT_INVOICES)}
        />
        {profiles.length > 0 && (
          <NavigationCard
            icon={<Icon.RecurringCharge size={Icon.SIZE.SM} />}
            title="Recurring profiles"
            description="Manage subscriptions and automatic payments"
            badge={activeProfilesCount ? <T count={activeProfilesCount}>%count% active</T> : undefined}
            onClick={() => navigate(ROUTES.CLIENT_RECURRING_PROFILES)}
          />
        )}
        {rebillingEnabled && (
          <NavigationCard
            icon={<Icon.StorageBox size={Icon.SIZE.SM} />}
            title="Saved payment methods"
            description="Review your saved payment methods and remove any you no longer use"
            badge={
              methodsCount ? (
                <T count={methodsCount}>{methodsCount === 1 ? "%count% card" : "%count% cards"}</T>
              ) : undefined
            }
            onClick={() => navigate(ROUTES.CLIENT_PAYMENT_METHODS)}
          />
        )}
      </Stack>
    </Container>
  );
};

export default ClientPayments;
